import type { Fetch } from './chain.ts';
import { GLEIF_API, isValidLei, lookupLei, normaliseLei, parseGleifRecord } from './gleif.ts';

/**
 * The guided onboarding (/start/, README "Onboarding paths"): three answers
 * pick one of five paths, each a short checklist that ends in /onboard/ (the
 * proof + 1 CCD session), the domains and the binding record.
 *
 *   A  LEI + wallet with a company identity      ccd → connect → domains → binding
 *   B  LEI + wallet, no company identity yet     identity → approval → account → ccd → A's steps
 *   C  LEI, no wallet                            wallet → B
 *   D  no LEI, wallet                            lei → B
 *   E  no LEI, no wallet                         wallet → lei → B
 *
 * "Not sure" never blocks: the page resolves it where it can (a GLEIF lookup
 * for the LEI, detecting the browser wallet, checking the account's identity),
 * and until then it takes the path that still works: an unknown LEI or wallet
 * counts as "not yet" (the step for it ticks itself as soon as PageID sees
 * it), an unknown company identity as "probably" (the connect step's proof
 * tells, and a wallet without one is sent to "Get a company identity").
 */
export type Answer = 'yes' | 'no' | 'unsure';
export type PathId = 'A' | 'B' | 'C' | 'D' | 'E';
export type StepId = 'wallet' | 'lei' | 'identity' | 'approval' | 'account' | 'ccd' | 'connect' | 'domains' | 'binding';
export type Answers = { lei?: Answer | null; wallet?: Answer | null; companyId?: Answer | null };

export const STEP_IDS: readonly StepId[] = ['wallet', 'lei', 'identity', 'approval', 'account', 'ccd', 'connect', 'domains', 'binding'];
const FINISH: StepId[] = ['ccd', 'connect', 'domains', 'binding'];
const IDENTITY: StepId[] = ['identity', 'approval', 'account', ...FINISH];

export const PATH_STEPS: Record<PathId, StepId[]> = {
  A: FINISH,
  B: IDENTITY,
  C: ['wallet', ...IDENTITY],
  D: ['lei', ...IDENTITY],
  E: ['wallet', 'lei', ...IDENTITY],
};

export const PATH_TITLES: Record<PathId, string> = {
  A: 'Ready to connect',
  B: 'Get a company identity first',
  C: 'Install the wallet, then get a company identity',
  D: 'Get an LEI, then a company identity',
  E: 'Wallet, LEI and company identity, step by step',
};

/** Which questions still need an answer before a path is known (the company identity question only for wallet owners). */
export function openQuestions(a: Answers): Array<keyof Answers> {
  const out: Array<keyof Answers> = [];
  if (!a.lei) out.push('lei');
  if (!a.wallet) out.push('wallet');
  if (a.wallet === 'yes' && !a.companyId) out.push('companyId');
  return out;
}

/**
 * The path for a set of answers, or null while a needed answer is missing.
 * All nine LEI × wallet combinations (and the company identity answer for
 * wallet owners) map to exactly one path; see the module comment for "unsure".
 */
export function choosePath(a: Answers): PathId | null {
  if (openQuestions(a).length) return null;
  const lei = a.lei === 'yes';
  const wallet = a.wallet === 'yes';
  if (!wallet) return lei ? 'C' : 'E';
  if (!lei) return 'D';
  return a.companyId === 'no' ? 'B' : 'A';
}

/** The steps of a path and how far along it is: the first step not done is the current one. */
export function pathProgress(path: PathId, done: Iterable<string>): { steps: StepId[]; done: StepId[]; current: StepId | null; index: number; total: number } {
  const d = new Set(done);
  const steps = PATH_STEPS[path];
  const doneSteps = steps.filter((s) => d.has(s));
  const current = steps.find((s) => !d.has(s)) ?? null;
  return { steps, done: doneSteps, current, index: current ? steps.indexOf(current) + 1 : steps.length, total: steps.length };
}

// ---- CCD for the onboarding -------------------------------------------------------

/**
 * What PageID's onboarding costs from the company account, in micro-CCD:
 * the proof request's anchor (≈ 4.5 CCD, browser wallet path), the session
 * transfer (≈ 1 CCD), the binding record (≈ 5 CCD), plus small fees: about
 * 11 CCD in all. Scanning is free.
 */
export const ONBOARDING_CCD_MICRO = 11_000_000;
export const WALLET_PROXY = 'https://wallet-proxy.mainnet.concordium.software';
export const ACCOUNT_ADDRESS = /^[1-9A-HJ-NP-Za-km-z]{50}$/;

/**
 * The spendable balance of an account (micro-CCD) from the wallet-proxy's
 * `/v0/accBalance/<address>` (CORS-open): the finalized amount minus what is
 * locked in a release schedule or staked.
 */
export function parseAccBalance(body: unknown): number {
  const b = (body as { finalizedBalance?: Record<string, any> } | null)?.finalizedBalance;
  if (!b || b.accountAmount === undefined) throw new Error('the wallet-proxy answer has no finalized balance');
  const amount = Number(b.accountAmount);
  const locked = Number(b.accountReleaseSchedule?.total ?? 0);
  const staked = Number(b.accountBaker?.stakedAmount ?? b.accountDelegation?.stakedAmount ?? 0);
  return Math.max(0, amount - Math.max(locked, staked));
}

export async function ccdBalance(address: string, fetchFn: Fetch, base = WALLET_PROXY): Promise<number> {
  if (!ACCOUNT_ADDRESS.test(address)) throw new Error('that is not a Concordium account address (50 characters, starts with a digit)');
  const res = await fetchFn(`${base}/v0/accBalance/${address}`);
  if (res.status === 400 || res.status === 404) throw new Error('no such Concordium account on mainnet');
  if (!res.ok) throw new Error(`the wallet-proxy answered HTTP ${res.status}`);
  return parseAccBalance(await res.json());
}

/** The CCD step ticks itself when the account holds at least the onboarding's ~11 CCD. */
export const enoughCcd = (micro: number, need = ONBOARDING_CCD_MICRO) => Number.isFinite(micro) && micro >= need;

export const formatCcd = (micro: number) => `${(micro / 1e6).toLocaleString('en-GB', { maximumFractionDigits: micro % 1e6 === 0 ? 0 : 2 })} CCD`;

// ---- finding a company's LEI at GLEIF ------------------------------------------------

export type LeiMatch = { lei: string; legalName: string; country: string | null; city: string | null; registeredAs: string | null; status: string | null; exact: boolean };

/** A national registration number typed as digits, maybe with a two-letter country prefix and spaces ("DK 45 74 97 54"). */
export function registrationNumber(q: string): { number: string; country: string | null } | null {
  const s = q.trim().toUpperCase().replace(/[\s.\-]/g, '');
  const m = s.match(/^([A-Z]{2})?(\d{6,12})$/);
  return m ? { number: m[2], country: m[1] ?? null } : null;
}

const toMatch = (item: unknown, exact: boolean): LeiMatch => {
  const e = parseGleifRecord({ data: item });
  return { lei: e.lei, legalName: e.legalName, country: e.country || null, city: e.city, registeredAs: e.registeredAs, status: e.entityStatus || null, exact };
};

/**
 * Finds a company's LEI at GLEIF (free, CORS-open, no key) from what a person
 * types: the LEI itself, a national registration number
 * (`filter[entity.registeredAs]`, in `country` first when one is known, then anywhere), or the
 * legal name (exact `filter[entity.legalName]`, else GLEIF's fuzzy completions).
 * Returns at most 5 matches, exact ones first; [] = no LEI found.
 */
export async function findLei(query: string, fetchFn: Fetch, opts: { country?: string | null; base?: string } = {}): Promise<LeiMatch[]> {
  const base = opts.base ?? GLEIF_API;
  const api = base.replace(/\/lei-records$/, '');
  const q = query.trim();
  if (q.length < 2) throw new Error('type your LEI, registration number or company name');
  const get = async (url: string) => {
    const res = await fetchFn(url, { headers: { accept: 'application/vnd.api+json' } });
    if (!res.ok) throw new Error(`GLEIF answered HTTP ${res.status}`);
    return (await res.json()) as { data?: unknown[] };
  };
  const lei = normaliseLei(q);
  if (isValidLei(lei)) {
    try {
      const e = await lookupLei(lei, fetchFn, base);
      return [{ lei: e.lei, legalName: e.legalName, country: e.country || null, city: e.city, registeredAs: e.registeredAs, status: e.entityStatus || null, exact: true }];
    } catch (err) {
      if (/no record/.test((err as Error).message)) return [];
      throw err;
    }
  }
  const reg = registrationNumber(q);
  if (reg) {
    const countries = [...new Set([reg.country ?? opts.country ?? null, null])];
    for (const c of countries) {
      const url = `${base}?filter[entity.registeredAs]=${encodeURIComponent(reg.number)}${c ? `&filter[entity.legalAddress.country]=${c}` : ''}&page[size]=5`;
      const items = (await get(url)).data ?? [];
      if (items.length) return items.slice(0, 5).map((i) => toMatch(i, true));
    }
    return [];
  }
  const exact = (await get(`${base}?filter[entity.legalName]=${encodeURIComponent(q)}&page[size]=5`)).data ?? [];
  if (exact.length) return exact.slice(0, 5).map((i) => toMatch(i, true));
  const fuzzy = (await get(`${api}/fuzzycompletions?field=entity.legalName&q=${encodeURIComponent(q)}`)).data ?? [];
  return fuzzy.slice(0, 5).flatMap((f) => {
    const x = f as { attributes?: { value?: string }; relationships?: { 'lei-records'?: { data?: { id?: string } } } };
    const id = x.relationships?.['lei-records']?.data?.id;
    return id && x.attributes?.value ? [{ lei: id, legalName: x.attributes.value, country: null, city: null, registeredAs: null, status: null, exact: false }] : [];
  });
}

// ---- does an account hold a company identity? --------------------------------------

/**
 * From a node's account info (`GetAccountInfo`, the SDK's AccountInfo shape):
 * the identity provider of the account's first credential and whether that
 * credential is a COMPANY identity (Global FinReg, idp 3, with committed or
 * revealed company attributes: legalName / businessNumber) and holds an LEI.
 * A personal identity, even from idp 3, is `company: false`.
 */
export function accountIdentity(info: unknown, companyIdp = 3): { idp: number; company: boolean; lei: boolean } {
  const creds = Object.entries((info as { accountCredentials?: Record<string, { value?: { contents?: any } }> })?.accountCredentials ?? {}).sort(([a], [b]) => Number(a) - Number(b));
  const c = creds[0]?.[1]?.value?.contents;
  if (!c || typeof c.ipIdentity !== 'number') throw new Error('the account has no credential');
  const attrs = new Set([...Object.keys(c.commitments?.cmmAttributes ?? {}), ...Object.keys(c.policy?.revealedAttributes ?? {})]);
  const company = c.ipIdentity === companyIdp && (attrs.has('legalName') || attrs.has('businessNumber'));
  return { idp: c.ipIdentity, company, lei: company && attrs.has('lei') };
}
